/**
 * JURO — scroll reveal
 * ------------------------------------------------------------------
 * One IntersectionObserver for the whole page. It sets an attribute the first
 * time an element comes near the viewport and then stops watching it — there
 * is no scroll listener, no rAF loop and no per-element observer, so the cost
 * is a few microseconds at load and nothing at all afterwards.
 *
 * Elements opt in by carrying data-juro-in or data-juro-stagger. The CSS in
 * juro-motion.css does the animating; this file only flips state.
 *
 * Two deliberate choices:
 *
 *   - Anything already on screen at load is revealed immediately, without a
 *     transition. Animating the hero on arrival delays the first thing a
 *     visitor reads for no benefit, and on a slow connection it can look like
 *     a rendering fault.
 *
 *   - Under prefers-reduced-motion nothing is observed at all. The CSS
 *     already forces these elements visible, so the work is pointless.
 */

(() => {
  const REVEAL = '[data-juro-in], [data-juro-stagger]';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Which attribute this element animates with. */
  const attr = (el) => (el.hasAttribute('data-juro-stagger') ? 'data-juro-stagger' : 'data-juro-in');

  /** Reveal without animating — for content that is already in view. */
  function revealInstantly(el) {
    const previous = el.style.transition;
    el.style.transition = 'none';
    el.setAttribute(attr(el), 'true');
    // Force a reflow so the attribute lands while transitions are still off,
    // then restore. Without this the browser batches both and animates anyway.
    void el.offsetHeight;
    el.style.transition = previous;
  }

  function init() {
    const elements = document.querySelectorAll(REVEAL);
    if (!elements.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      for (const el of elements) el.setAttribute(attr(el), 'true');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute(attr(entry.target), 'true');
          observer.unobserve(entry.target);
        }
      },
      // Start a little before the element arrives so it has finished moving by
      // the time it is properly in view, rather than animating under the eye.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );

    const fold = window.innerHeight;
    for (const el of elements) {
      if (el.getBoundingClientRect().top < fold) {
        revealInstantly(el);
        continue;
      }
      observer.observe(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
